import React from "react";

import { getCommunicationsUsage } from "../../../../../services/api-communications";
import type { TGetCommunicationsUsageResponse } from "../../../../../services/api-communications";
import type { TSession } from "../../../../../utils/types";
import { toUserFacingErrorMessage } from "../utils/errors";
import {
  formatDateInputValue,
  toDateInputFromPeriodValue,
  toIsoDateOrNull,
} from "../utils/formatters";
import { parseUsagePayload } from "../utils/parsers";

type UseUsageSummaryParams = {
  session: TSession | null | undefined;
};

type UseUsageSummaryReturn = {
  defaultUsageStartDate: string;
  defaultUsageEndDate: string;
  usageStartDate: string;
  usageEndDate: string;
  usageSummary: TGetCommunicationsUsageResponse | null;
  usageLoading: boolean;
  usageError: string | null;
  setUsageStartDate: React.Dispatch<React.SetStateAction<string>>;
  setUsageEndDate: React.Dispatch<React.SetStateAction<string>>;
  loadUsageSummary: (startDate: string, endDate: string) => Promise<void>;
  loadUsageSummaryForBillingPeriod: () => Promise<void>;
};

export const useUsageSummary = ({
  session,
}: UseUsageSummaryParams): UseUsageSummaryReturn => {
  const now = React.useMemo(() => new Date(), []);
  const defaultUsageEndDate = React.useMemo(
    () => formatDateInputValue(now),
    [now],
  );
  const defaultUsageStartDate = React.useMemo(
    () => formatDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)),
    [now],
  );

  const [usageStartDate, setUsageStartDate] = React.useState<string>(
    defaultUsageStartDate,
  );
  const [usageEndDate, setUsageEndDate] =
    React.useState<string>(defaultUsageEndDate);
  const [usageSummary, setUsageSummary] =
    React.useState<TGetCommunicationsUsageResponse | null>(null);
  const [usageLoading, setUsageLoading] = React.useState<boolean>(true);
  const [usageError, setUsageError] = React.useState<string | null>(null);

  const loadUsageSummary = React.useCallback(
    async (startDate: string, endDate: string) => {
      if (!session) {
        return;
      }

      const requestedStartDate = toIsoDateOrNull(startDate);
      const requestedEndDate = toIsoDateOrNull(endDate);

      if (!requestedStartDate || !requestedEndDate) {
        setUsageError("Enter dates in YYYY-MM-DD format.");
        return;
      }

      if (requestedStartDate > requestedEndDate) {
        setUsageError("Start date must be on or before end date.");
        return;
      }

      setUsageLoading(true);
      setUsageError(null);

      try {
        const usageResponse = await getCommunicationsUsage(session, {
          start_date: requestedStartDate,
          end_date: requestedEndDate,
        });
        const parsedUsage = parseUsagePayload(usageResponse);
        if (!parsedUsage) {
          setUsageSummary(null);
          setUsageError("Usage summary is unavailable right now.");
          return;
        }
        setUsageSummary(parsedUsage);
      } catch (error) {
        setUsageSummary(null);
        setUsageError(
          toUserFacingErrorMessage(
            error,
            "Unable to load usage summary right now.",
          ),
        );
      } finally {
        setUsageLoading(false);
      }
    },
    [session],
  );

  const loadUsageSummaryForBillingPeriod = React.useCallback(async () => {
    if (!session) {
      return;
    }

    setUsageLoading(true);
    setUsageError(null);

    let resolvedFromBillingPeriod = false;

    try {
      const usageResponse = await getCommunicationsUsage(session);
      const parsedUsage = parseUsagePayload(usageResponse);
      const billingPeriodStart = toDateInputFromPeriodValue(
        parsedUsage?.period_start,
      );
      const billingPeriodEnd = toDateInputFromPeriodValue(
        parsedUsage?.period_end,
      );

      if (parsedUsage && billingPeriodStart && billingPeriodEnd) {
        setUsageSummary(parsedUsage);
        setUsageStartDate(billingPeriodStart);
        setUsageEndDate(billingPeriodEnd);
        resolvedFromBillingPeriod = true;
      }
    } catch {
      // Fall back to the local default range if billing period metadata is unavailable.
    }

    setUsageLoading(false);

    if (resolvedFromBillingPeriod) {
      return;
    }

    await loadUsageSummary(defaultUsageStartDate, defaultUsageEndDate);
  }, [defaultUsageEndDate, defaultUsageStartDate, loadUsageSummary, session]);

  return {
    defaultUsageStartDate,
    defaultUsageEndDate,
    usageStartDate,
    usageEndDate,
    usageSummary,
    usageLoading,
    usageError,
    setUsageStartDate,
    setUsageEndDate,
    loadUsageSummary,
    loadUsageSummaryForBillingPeriod,
  };
};
